/**
 * pricescale.ts — how many decimals THIS asset's prices carry, for the pane's price axis.
 *
 * CARD `t_ccfe3378` (Andrei's screenshot, 2026-09-05): PUMP (~0.0041) rendered with every
 * right-axis label, the crosshair label and the last-price tag reading `0.00` — "невозможно
 * понять, где мы находимся". The candle series was created with no `priceFormat`, so it sat on
 * lightweight-charts' default `{ type: 'price', precision: 2, minMove: 0.01 }`. BTC/SOL/xyz never
 * exposed it because they trade in dollars; any sub-cent name was unreadable.
 *
 * THE NUMBER COMES FROM THE SERVER. `/api/bars` serves `meta.px_decimals`, derived from the venue's
 * own quoting rule over its `szDecimals` (`chartapp/api/price_precision.py`: max(5, int digits)
 * significant figures, decimals capped at `6 - szDecimals`), so the browser never calls the venue
 * and there is no per-asset table anywhere. `precisionFor` is the ONE reader of that field, and the
 * fallback when a document lacks it (an older cached response, a non-venue feed) is the decimal
 * count the loaded closes THEMSELVES carry — the same reading `overlays.priceText` makes.
 *
 * NEVER A FIXED `toFixed(8)`: BTC's axis would read `79592.00000000` and lightweight-charts'
 * `minMove` would go to 1e-8, which makes its axis tick generator produce dense, unreadable ticks.
 * NEVER a clamp to 2 either — that is the defect.
 */

/** Ceiling on what any fallback may answer, matching `priceText`'s `Math.min(8, ...)`. */
export const MAX_PRICE_DECIMALS = 8;

/** Decimal count of one finite value's shortest round-trip string; -1 when unreadable. */
export function decimalsOf(x: number): number {
  if (!Number.isFinite(x)) return -1;
  let s = String(x);
  if (s.includes('e') || s.includes('E')) {
    // `1e-7`: expand, because a tiny ratio price is exactly the population this exists for.
    s = x.toFixed(MAX_PRICE_DECIMALS).replace(/0+$/, '');
  }
  const dot = s.indexOf('.');
  if (dot < 0) return 0;
  return s.length - dot - 1 - (s.match(/0+$/)?.[0].length ?? 0);
}

/** The most decimals any of `values` carries, capped; null when nothing is readable. */
export function decimalsFromValues(values: readonly number[]): number | null {
  let best = -1;
  for (const v of values) best = Math.max(best, decimalsOf(v));
  return best < 0 ? null : Math.min(MAX_PRICE_DECIMALS, best);
}

/**
 * The precision the pane applies, from the bars response.
 *
 * Order: the server's `px_decimals` when it is a non-negative integer; else the closes' own decimal
 * count; else `null`, which means "leave the series as it is" — a caller must not turn a missing
 * answer into `2`, since 2 is the library default that produced the defect.
 */
export function precisionFor(
  meta: { px_decimals?: unknown } | null | undefined,
  closes: readonly number[],
): number | null {
  const served = meta?.px_decimals;
  if (typeof served === 'number' && Number.isInteger(served) && served >= 0) {
    return Math.min(MAX_PRICE_DECIMALS, served);
  }
  return decimalsFromValues(closes);
}

/** The lightweight-charts `priceFormat` for a decimal count. `minMove` is one unit of the last place. */
export function priceFormatFor(decimals: number): { type: 'price'; precision: number; minMove: number } {
  const p = Math.max(0, Math.min(MAX_PRICE_DECIMALS, Math.trunc(decimals)));
  // `Number('1e-p')` rather than `10 ** -p`, MEASURED (node 2026-09-05): `10 ** -4` evaluates to
  // `0.00009999999999999999` and `10 ** -5` to `0.000009999999999999999` — exactly SOL's and a
  // 5-decimal name's `minMove`. The literal-string parse is the correctly rounded double for every p.
  const minMove = p === 0 ? 1 : Number(`1e-${p}`);
  return { type: 'price', precision: p, minMove };
}
